import type { ColumnsType } from 'antd/es/table';
import { useCallback, useMemo } from 'react';

import type { PayoutReasonCodeItem, PayoutReasonCodeStatItem } from 'api';

import { useOrgNavigate } from 'hooks/useOrgNavigate';

import { StatsTable } from 'components/ui/organisms/StatsTable';
interface DrilldownTableProps {
  stateId: string;
  title: string;
  data: PayoutReasonCodeStatItem;
  className?: string;
  onHeaderClick: () => void;
}

export const DrilldownTable = ({
  stateId,
  title,
  data,
  className,
  onHeaderClick
}: DrilldownTableProps) => {
  const total = data ? data.meta.count : 'N/A';
  const orgNavigate = useOrgNavigate();
  const defaultHeaderCell = () => ({ className: '!border-y' });
  const onReasonCodeClick = useCallback(
    (reasonCodeId: number) => {
      orgNavigate(`/payouts?reasonCode=${reasonCodeId}&state=${stateId}`);
    },
    [orgNavigate, stateId]
  );

  const columns = useMemo<ColumnsType<PayoutReasonCodeItem>>(
    () => [
      {
        key: 'name',
        dataIndex: 'name',
        className: '!text-left align-top w-2/3 xl:w-4/6',
        onHeaderCell: defaultHeaderCell,
        title: () => (
          <div className="py-2 pl-2 text-left text-xs font-normal">Reason</div>
        ),
        render: (_, record) => (
          <a
            className="cursor-pointer pl-2 font-light text-content-primary hover:text-content-brand-soft"
            onClick={() => onReasonCodeClick(record.id)}
            role="link"
          >
            {record.name}
          </a>
        ),
        ellipsis: true
      },
      {
        key: 'count',
        dataIndex: 'count',
        className: 'p-4 text-center align-top ',
        onHeaderCell: defaultHeaderCell,
        title: () => (
          <div className="py-2 text-left text-xs font-normal">
            Count: {total}
          </div>
        ),
        render: (value: number) => (
          <div className="text-left font-medium">{value}</div>
        )
      },
      {
        key: 'percentage',
        dataIndex: 'percentage',
        className: 'p-4 text-center align-top',
        onHeaderCell: defaultHeaderCell,
        title: () => (
          <div className="py-2 text-left text-xs font-normal">Total</div>
        ),
        render: (value: number) => {
          if (value === null || value === undefined) return null;

          const rounded = Math.round(value);
          return (
            <div className="text-left font-medium" title={`${value}%`}>
              {rounded}%
            </div>
          );
        }
      }
    ],
    [onReasonCodeClick, total]
  );

  return (
    <>
      {data?.items && (
        <StatsTable
          columns={columns}
          data={data.items}
          title={title}
          onHeaderClick={onHeaderClick}
          totalPercentage={data.meta.percentage}
          className={className}
        />
      )}
    </>
  );
};
